import { ElementType, InputHTMLAttributes } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import * as Select from '@radix-ui/react-select'

import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'
import { WPGravityFormField } from '@/types'

type FormControlProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> &
  Partial<
    WPGravityFormField & {
      error?: boolean
      size: 'small' | 'large' | 'medium'
      options?: { value: string; text: string }[]
      // eslint-disable-next-line no-unused-vars
      onChange?: (e: any) => void
      // eslint-disable-next-line no-unused-vars
      onBlur?: (e: any) => void
      theme?: 'dark' | 'light'
    }
  >

const FormControl = ({
  id,
  type,
  label,
  checked,
  options,
  placeholder,
  className,
  children,
  theme = 'light',
  error,
  size,
  ...props
}: FormControlProps) => {
  const Component = (type === 'textarea' ? 'textarea' : 'input') as ElementType

  return (
    <div
      className={cn(
        className,
        {
          hidden: type === 'hidden'
        },
        {
          'col-span-2 lg:col-span-1': size === 'medium',
          'col-span-2': size === 'large'
        }
      )}
      style={
        {
          '--tw-ring-color': '#4d4d4d'
        } as React.CSSProperties
      }
    >
      {type === 'consent' ? (
        <label className="relative flex items-center space-x-[1rem]">
          <input
            id={id}
            type="checkbox"
            checked={checked}
            className={cn([
              'size-[2.4rem] flex-[0_0_2.4rem] opacity-0 placeholder:text-text-primary-light [&:checked+div]:after:bg-border-secondary-light [&:checked+div_svg]:opacity-100'
            ])}
            {...props}
          />
          <div
            className={cn([
              'absolute left-0 top-0 !m-0 size-[2.4rem] shrink-0 cursor-pointer border border-surface-accent-invert-light',
              {
                'border-error': error
              }
            ])}
          >
            <SVGIcon
              symbol="check-icon"
              className={cn([
                'fill-surface-icon-light absolute left-1/2 top-1/2 z-10 size-[1.6rem] -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity'
              ])}
            />
          </div>
          {label ? (
            <div className="flex-[1_1_auto]">
              <Typography
                variant="small"
                className={cn([
                  '[&>#required-data]:mt-[1rem] [&>#required-data]:inline-block',
                  {
                    'text-error': error
                  }
                ])}
              >
                {label}
              </Typography>
            </div>
          ) : null}
        </label>
      ) : type === 'select' ? (
        <Select.Root value={props.value as string} onValueChange={props.onChange}>
          <div className="flex">
            <Typography variant="body">{label}</Typography>
          </div>
          <Select.Trigger
            id={id}
            /* className={cn([
              'flex w-full appearance-none items-center justify-between bg-white-light px-spacing-xs py-[0.9rem] text-body text-text-primary-light outline-none',
              'rounded-radius-minimal border border-surface-accent-invert-light',
              'focus:border-text-accent-light focus:ring-2',
              {
                'border-error focus:ring-error/50': error
              }
            ])} */
            className={cn([
              'text-regular appearance-none font-roboto',
              'flex w-full items-center justify-between rounded-radius-minimal bg-transparent px-spacing-xs py-[0.9rem] outline-none',
              'border border-surface-accent-invert-light dark:border-surface-accent-dark/50',
              'placeholder:text-text-primary-light dark:placeholder:text-text-primary-dark/90',
              'focus:border-text-accent-light dark:focus:border-text-accent-dark',
              'focus:ring-2 focus:ring-text-accent-light/50 dark:focus:ring-text-accent-dark/50',
              {
                '!border-error focus:ring-2 focus:ring-error/50': error
              }
            ])}
          >
            <Select.Value placeholder={placeholder} />
            <Select.Icon>
              <ChevronDownIcon
                className={cn([
                  {
                    'text-dark-500': theme === 'light',
                    'text-white': theme === 'dark'
                  }
                ])}
              />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content
              data-lenis-prevent
              className={cn([
                'z-50 overflow-hidden border border-surface-accent-invert-light bg-white-light shadow-md'
              ])}
            >
              <Select.ScrollUpButton className="flex h-[25px] items-center justify-center">
                <ChevronUpIcon />
              </Select.ScrollUpButton>

              <Select.Viewport className="bg-white-light">
                {options?.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    className={cn([
                      'relative flex select-none items-center px-[2rem] py-[0.75rem] text-body outline-none',
                      'data-[highlighted]:bg-gray-100 hover:cursor-pointer'
                    ])}
                  >
                    <Select.ItemText>{option.text}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex h-[25px] items-center justify-center">
                <ChevronDownIcon />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      ) : (
        <div>
          <div className="flex">
            <Typography variant="body">{label}</Typography>
          </div>
          <Component
            id={id}
            type={type}
            placeholder={placeholder}
            className={cn([
              'text-regular appearance-none font-roboto',
              'flex w-full rounded-radius-minimal bg-transparent px-spacing-xs py-[0.9rem] outline-none',
              'border border-surface-accent-invert-light dark:border-surface-accent-dark/50',
              'placeholder:text-text-primary-light dark:placeholder:text-text-primary-dark/90',
              'focus:border-text-accent-light dark:focus:border-text-accent-dark',
              'focus:ring-2 focus:ring-text-accent-light/50 dark:focus:ring-text-accent-dark/50',
              {
                '!border-error focus:ring-2 focus:!ring-error/50': error
              }
            ])}
            {...props}
          />
        </div>
      )}
      {children}
    </div>
  )
}

export default FormControl
